'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { Mail, Heart } from 'lucide-react';
import Link from 'next/link';
import Image from 'next/image';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="from-background via-background/95 to-card/20 border-border/50 relative border-t bg-gradient-to-br">
      {/* Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="bg-primary/5 absolute -right-20 -bottom-20 h-40 w-40 rounded-full blur-3xl" />
        <div className="bg-secondary/5 absolute -top-20 -left-20 h-40 w-40 rounded-full blur-3xl" />
      </div>

      <div className="relative container mx-auto px-4 py-16 md:px-6 lg:px-8">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 items-center gap-8 md:grid-cols-3">
          {/* Logo Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="flex flex-col items-center space-y-4 md:items-start"
          >
            <div className="flex items-center space-x-2">
              <Link
                href="/"
                className="bg-background border-primary hover:bg-primary/10 relative z-20 mr-4 flex cursor-pointer items-center space-x-2 rounded-full border p-2 transition-all duration-300 hover:scale-105 hover:border-blue-400"
              >
                <Image src="/images/EJ.svg" alt="logo" width="35" height="35" />
              </Link>
            </div>
            <p className="text-muted-foreground max-w-xs text-center text-sm md:text-left">
              Crafting exceptional digital experiences with passion and
              precision.
            </p>
          </motion.div>

          {/* Contact Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex flex-col items-center space-y-4 md:items-center"
          >
            <div className="text-foreground text-lg font-semibold">
              Get in Touch
            </div>
            <div className="flex flex-col items-center space-y-3">
              <Link
                href="mailto:eric@ejmalek.com"
                className="text-muted-foreground hover:text-primary group flex items-center space-x-2 transition-colors duration-300"
              >
                <Mail className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
                <span className="text-sm">EMAIL</span>
              </Link>
              <Link
                href="https://linkedin.com/in/ejmalek"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-primary group flex items-center space-x-2 transition-colors duration-300"
              >
                <Image
                  src="/images/LinkedIn.svg"
                  alt="LinkedIn"
                  width={16}
                  height={16}
                  className="transition-transform duration-300 group-hover:scale-110"
                />
                <span className="text-sm">LinkedIn</span>
              </Link>
            </div>
          </motion.div>

          {/* Quick Links Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="flex flex-col items-center space-y-4 md:items-end"
          >
            <div className="text-foreground text-lg font-semibold">
              Quick Links
            </div>
            <div className="flex flex-col items-center space-y-2 md:items-end">
              <Link
                href="/about"
                className="text-muted-foreground hover:text-primary text-sm transition-colors duration-300"
              >
                About
              </Link>
              <Link
                href="/projects"
                className="text-muted-foreground hover:text-primary text-sm transition-colors duration-300"
              >
                Projects
              </Link>
              <Link
                href="/case-studies"
                className="text-muted-foreground hover:text-primary text-sm transition-colors duration-300"
              >
                Case Studies
              </Link>
              <Link
                href="/contact"
                className="text-muted-foreground hover:text-primary text-sm transition-colors duration-300"
              >
                Contact
              </Link>
            </div>
          </motion.div>
        </div>

        {/* Divider */}
        <motion.div
          initial={{ opacity: 0, scaleX: 0 }}
          whileInView={{ opacity: 1, scaleX: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="via-border my-8 h-px w-full bg-gradient-to-r from-transparent to-transparent"
        />

        {/* Copyright Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="flex flex-col items-center justify-between space-y-4 md:flex-row md:space-y-0"
        >
          <div className="text-muted-foreground flex items-center space-x-2 text-sm">
            <span>© {currentYear} EJ Malek. All rights reserved.</span>
          </div>

          <div className="text-muted-foreground flex items-center space-x-2 text-sm">
            <span>Made with</span>
            <Heart className="h-4 w-4 animate-pulse text-red-500" />
            <span>in TEXAS</span>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
